import { useEffect, useState } from 'react'
import { Loader2, Pencil, Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import { estimateTicket, formatEur, jaSingleColumn, parseBudget } from '@/lib/quiniela'
import type { ProposalState } from '@/hooks/useQuiniela'
import type { ProposalSelection, QuinielaCurrent, QuinielaMatchView } from '@/types/quiniela'
import { KIND_STYLE, MatchProbRow } from './MatchProbRow'
import { PlenoCard } from './PlenoCard'
import { TicketSummary } from './TicketSummary'
import { PlayTicketAction } from './PlayTicket'
import { isPleno } from '@/lib/quiniela'

const QUICK = [5, 10, 15, 20]
const MAX_BUDGET = 500

function Legend() {
  return (
    <div className="flex items-center gap-3 pb-1 text-[10px] font-semibold text-text-dim">
      <span className="inline-flex items-center gap-1"><i className="h-2 w-2 rounded-sm bg-brand" /> Simple</span>
      <span className="inline-flex items-center gap-1"><i className="h-2 w-2 rounded-sm bg-cyan" /> Doble</span>
      <span className="inline-flex items-center gap-1"><i className="h-2 w-2 rounded-sm bg-violet" /> Triple</span>
    </div>
  )
}

/** Créala con John Anthony: el usuario fija presupuesto y JA devuelve la mejor propuesta que cabe (optimizador
 * ya existente). El resultado es el protagonista: apuestas, coste real, sobrante y dobles/triples a la vista. */
export function BudgetPanel({
  current, matches, state, onGenerate, onAcceptCurrent, onReset,
}: {
  current: QuinielaCurrent
  matches: QuinielaMatchView[]
  state: ProposalState
  onGenerate: (budget: number) => void
  onAcceptCurrent: (budget: number) => void
  onReset: () => void
}) {
  const [quick, setQuick] = useState<number | null>(10)
  const [custom, setCustom] = useState('')
  const [editing, setEditing] = useState(true)
  const rules = current.rules
  const customValue = custom.trim() ? parseBudget(custom) : null
  const budget = custom.trim() ? customValue : quick
  const tooLow = budget != null && budget < rules.min_cost_eur
  const tooHigh = budget != null && budget > MAX_BUDGET
  const invalid = budget == null || tooLow || tooHigh
  const busy = state.phase === 'loading' || state.phase === 'waiting_analysis'
  const ready = state.phase === 'ready'
  const maxColumns = budget ? Math.floor(budget / rules.column_price_eur + 1e-9) : 0

  // Al llegar la propuesta el formulario se recoge y el resultado pasa a primer plano.
  useEffect(() => { if (ready) setEditing(false) }, [ready])

  const form = (
    <section className="card space-y-3">
      <div>
        <p className="eyebrow text-brand">Presupuesto</p>
        <p className="text-[12px] text-text-muted mt-1 leading-snug">
          JA reparte simples, dobles y triples para maximizar la probabilidad con tu importe. Cada apuesta cuesta{' '}
          {formatEur(rules.column_price_eur)}; el mínimo del boleto son {rules.min_columns} apuestas ({formatEur(rules.min_cost_eur)}).
        </p>
      </div>

      <div className="grid grid-cols-4 gap-2" role="group" aria-label="Presupuestos rápidos">
        {QUICK.map((v) => {
          const on = !custom.trim() && quick === v
          return (
            <button
              key={v} type="button" aria-pressed={on}
              onClick={() => { setQuick(v); setCustom(''); if (state.phase !== 'idle' && !busy) onReset() }}
              className={cn('rounded-xl border py-2.5 text-sm font-black transition-colors',
                on ? 'bg-brand text-black border-brand' : 'bg-panel-elevated border-border text-text-muted hover:border-text-dim/50')}
            >
              <span className="figure">{v} €</span>
            </button>
          )
        })}
      </div>

      <label className="block">
        <span className="text-[10px] font-semibold text-text-dim">Otro importe</span>
        <div className={cn('mt-1 flex items-center rounded-xl border bg-panel-elevated px-3', custom.trim() ? 'border-brand/60' : 'border-border')}>
          <input
            inputMode="decimal" value={custom} placeholder="Por ejemplo 12,50"
            onChange={(e) => { setCustom(e.target.value); if (state.phase !== 'idle' && !busy) onReset() }}
            className="figure w-full bg-transparent py-2.5 text-sm text-text placeholder:text-text-dim/60 focus:outline-none"
            aria-label="Otro importe en euros"
          />
          <span className="text-sm font-bold text-text-dim">€</span>
        </div>
      </label>
      {(tooLow || tooHigh || (custom.trim() && customValue == null)) && (
        <p className="text-[11px] text-warn" role="alert">
          {tooLow ? `El mínimo es ${formatEur(rules.min_cost_eur)} (${rules.min_columns} apuestas).`
            : tooHigh ? `El máximo es ${formatEur(MAX_BUDGET)}.` : 'Escribe un importe válido.'}
        </p>
      )}
      {!invalid && <p className="text-[11px] text-text-dim">Con {formatEur(budget!)} caben hasta <span className="figure text-text-muted">{maxColumns}</span> apuestas.</p>}

      <button
        type="button" disabled={invalid || busy}
        onClick={() => budget && onGenerate(budget)}
        className="btn-brand-app w-full disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
        {busy ? 'Preparando tu Quiniela…' : 'Generar mi Quiniela con JA'}
      </button>
    </section>
  )

  const notices = (
    <>
      {state.phase === 'waiting_analysis' && (
        <section className="card border-brand/25 space-y-2" aria-live="polite">
          <p className="text-[13px] font-bold text-text">Analizando la jornada · <span className="figure">{state.processed}/{state.total}</span></p>
          <div className="h-1.5 rounded-full bg-border overflow-hidden">
            <div className="h-full bg-brand transition-[width] duration-500" style={{ width: `${Math.round((state.processed / Math.max(state.total, 1)) * 100)}%` }} />
          </div>
          <p className="text-[11px] text-text-muted leading-snug">
            Hay información nueva que incorporar. Tu propuesta se generará sola al terminar.
            {current.snapshot && budget != null && (
              <> ¿Prefieres no esperar? <button type="button" className="font-bold text-brand underline" onClick={() => onAcceptCurrent(budget)}>Usar el análisis actual</button>.</>
            )}
          </p>
        </section>
      )}
      {state.phase === 'error' && <p className="card text-xs text-warn" role="alert">{state.message}</p>}
    </>
  )

  if (!ready) return <div className="space-y-4 md:max-w-xl">{form}{notices}</div>

  return (
    <ProposalResult
      current={current} matches={matches} state={state}
      budgetBar={editing ? form : (
        <button
          type="button" onClick={() => setEditing(true)}
          className="flex w-full items-center justify-between rounded-xl border border-border bg-panel-elevated px-3.5 py-2.5 text-left"
        >
          <span className="text-[11px] text-text-dim">Presupuesto <span className="figure text-sm font-black text-text">{formatEur(state.proposal.budget_eur)}</span></span>
          <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-brand"><Pencil className="h-3 w-3" /> Cambiar presupuesto</span>
        </button>
      )}
      notices={notices}
    />
  )
}

function plural(n: number, one: string, many: string) { return `${n} ${n === 1 ? one : many}` }

function ProposalResult({
  current, matches, state, budgetBar, notices,
}: {
  current: QuinielaCurrent
  matches: QuinielaMatchView[]
  state: Extract<ProposalState, { phase: 'ready' }>
  budgetBar: React.ReactNode
  notices: React.ReactNode
}) {
  const p = state.proposal
  const byPos = new Map(matches.map((m) => [m.position, m]))
  const single = estimateTicket(matches, jaSingleColumn(matches), current.rules.column_price_eur).probability
  const vsSingle = single && single > 0 ? p.coverage_probability / single : null
  const per = Math.exp(Math.log(p.coverage_probability) / 14)
  const dobles = p.selections.filter((s: ProposalSelection) => s.kind === 'doble').map((s) => s.position)
  const triples = p.selections.filter((s: ProposalSelection) => s.kind === 'triple').map((s) => s.position)

  return (
    <div className="space-y-4 md:grid md:grid-cols-[22rem_minmax(0,1fr)] md:gap-5 md:items-start md:space-y-0">
      <div className="space-y-4 md:sticky md:top-20">
        {budgetBar}
        {notices}

        <section className="card-premium-glow space-y-3.5" data-testid="proposal-hero">
          <div className="flex items-baseline justify-between gap-2">
            <h3 className="font-display text-lg font-bold text-text leading-tight">Tu Quiniela con JA</h3>
            <span className="text-[10px] text-text-dim">Presupuesto <span className="figure">{formatEur(p.budget_eur)}</span></span>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <p className="text-[10px] font-semibold text-text-dim">Apuestas</p>
              <p className="figure text-[2rem] font-black leading-none text-text mt-1">{p.columns}</p>
            </div>
            <div>
              <p className="text-[10px] font-semibold text-text-dim">Coste real</p>
              <p className="figure text-[1.6rem] font-black leading-none text-brand mt-1.5">{formatEur(p.cost_eur)}</p>
            </div>
            <div>
              <p className="text-[10px] font-semibold text-text-dim">Sobrante</p>
              <p className="figure text-[1.6rem] font-black leading-none text-text-muted mt-1.5">{formatEur(Math.max(p.leftover_eur, 0))}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {triples.length > 0 && (
              <span className={cn('rounded-full border px-2.5 py-1 text-[11px] font-bold', KIND_STYLE.triple.chip)}>
                {plural(triples.length, 'triple', 'triples')} · {triples.length === 1 ? 'partido' : 'partidos'} {triples.join(', ')}
              </span>
            )}
            {dobles.length > 0 && (
              <span className={cn('rounded-full border px-2.5 py-1 text-[11px] font-bold', KIND_STYLE.doble.chip)}>
                {plural(dobles.length, 'doble', 'dobles')} · {dobles.length === 1 ? 'partido' : 'partidos'} {dobles.join(', ')}
              </span>
            )}
            {dobles.length + triples.length === 0 && (
              <span className={cn('rounded-full border px-2.5 py-1 text-[11px] font-bold', KIND_STYLE.simple.chip)}>Todo simples</span>
            )}
          </div>
          {state.updateAvailable && (
            <p className="text-[11px] text-cyan font-semibold">Hay información más reciente en camino: actualizaremos esta propuesta cuando termine el análisis.</p>
          )}
          <PlayTicketAction
            current={current}
            payload={{ origin: 'ja', proposal_id: p.id }}
            summary={{
              columns: p.columns, cost: p.cost_eur, dobles: dobles.length, triples: triples.length,
              selections: p.selections.map((s) => ({ position: s.position, signs: s.signs })),
              pleno: isPleno(p.pleno) ? { home: p.pleno.home, away: p.pleno.away } : null,
            }}
            played={current.my_tickets?.find((t) => t.proposal_id === p.id) ?? null}
          />
        </section>

        <TicketSummary
          variant="probability" columns={p.columns} cost={p.cost_eur} dobles={p.dobles} triples={p.triples}
          probability={p.coverage_probability} perMatchCoverage={per} leftover={p.leftover_eur} vsSingle={vsSingle}
        />
      </div>

      <div className="space-y-4 mt-4 md:mt-0">
        {p.missing_positions.length > 0 && (
          <p className="card text-[11px] text-warn leading-snug">
            Casilla {p.missing_positions.join(', ')}: JA aún no tiene probabilidades, así que se cubre con triple para no adivinar.
          </p>
        )}
        <section className="card">
          <Legend />
          {p.selections.map((s) => {
            const m = byPos.get(s.position)
            if (!m) return null
            // Las probabilidades mostradas son las del snapshot con el que se generó la propuesta.
            return <MatchProbRow key={s.position} match={{ ...m, probabilities: s.probabilities }} selected={s.signs} />
          })}
        </section>
        <PlenoCard match={byPos.get(15)} pleno={p.pleno} title="Pleno al 15 de JA" />
      </div>
    </div>
  )
}
